import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Box, Link, Typography } from '@mui/material';

import { TranslationKeys } from 'app/translations';

import type { Insurer, InsurerContact, OwnerInsurer } from 'app/types';

import DetailsPanelRow from '../../../../../components/DetailsPanelRow';
import InsurersContactPersonCard from '../../../Components/InsurersContactPersonCard/InsurersContactCard';

import { TypographyStyled } from './InsurerInformationTab.styles';

type InsurerDetailsProps = {
  insurer: Insurer | undefined;
  insurerOwner: OwnerInsurer | null | undefined;
  insurerContacts: InsurerContact[];
  isLoadingInsurer: boolean;
};

export const InsurerDetails = ({ insurer, insurerOwner, insurerContacts, isLoadingInsurer }: InsurerDetailsProps) => {
  const insurerInfoRow = {
    [TranslationKeys.insurers_insurerName]: insurer?.insurerName,
    [TranslationKeys.insurers_address]: insurer?.address,
    [TranslationKeys.insurers_zipCode]: insurer?.zipCode,
    [TranslationKeys.insurers_city]: insurer?.city,
    [TranslationKeys.insurers_insurerEmail]: insurer?.email,
    [TranslationKeys.insurers_insurerPhone]: insurer?.phone,
  };

  const portalLinkRows = (insurer?.portalLinks || []).map(portalLink => (
    <Link href={portalLink} key={portalLink}>
      <Typography variant="body1" color="primary" sx={{ overflowWrap: 'break-word' }}>
        {portalLink}
      </Typography>
    </Link>
  ));

  return (
    <>
      <DetailsPanelRow
        title={
          <TypographyStyled variant="body1">
            <FormattedMessage id={TranslationKeys.insurers_contactInformation} />
          </TypographyStyled>
        }
        rows={insurerInfoRow}
        loading={isLoadingInsurer}
      />

      {portalLinkRows.length > 0 && (
        <>
          <Box m={1} />
          <DetailsPanelRow
            title={
              <TypographyStyled variant="body1">
                <FormattedMessage id={TranslationKeys.insurers_portalLinks} />
              </TypographyStyled>
            }
            rows={portalLinkRows}
            loading={isLoadingInsurer}
          />
        </>
      )}

      {!!insurerOwner && (
        <>
          <Box m={1} />
          <DetailsPanelRow
            title={
              <TypographyStyled variant="body1">
                <FormattedMessage id={TranslationKeys.insurers_agencyNumber} />
              </TypographyStyled>
            }
            rows={{ [TranslationKeys.insurers_agencyNumber]: insurerOwner.agencyNumber }}
            loading={isLoadingInsurer}
          />
        </>
      )}

      {insurerContacts.length > 0 && (
        <>
          <Box m={1} />
          <DetailsPanelRow
            title={
              <TypographyStyled variant="body1">
                <FormattedMessage id={TranslationKeys.insurers_contactPersons} />
              </TypographyStyled>
            }
            rows={insurerContacts.map((insurerContact, index) => (
              <InsurersContactPersonCard contactPerson={insurerContact} key={index} />
            ))}
            loading={isLoadingInsurer}
          />
        </>
      )}
    </>
  );
};
